import { Box, Button, Flex, IconButton, Text } from "@chakra-ui/react"
import { useRecoilValue } from "recoil"
import { pages } from "../../../../common/constants/constants"
import { useBrandedTranslation } from "../../../../common/hooks/useBrandedTranslation"
import { useAuth, useGigya } from "../../../../common/hooks/useGigya"
import { gigyaIsEnabledState } from "../../../../common/store/authStore"
import { trackEvent } from "../../../../common/utils/gtag-utils"
import CustomLink from "../../../shared/CustomLink"
import LoadingIcon from "../../../shared/DownloadButton/LoadingIcon"
import { CArrowRight, CUserLoggedOff } from "../../../shared/Icons"
import UserAvatar from "./UserAvatar"

type Props = {
  isLink?: boolean
  handler?: () => void
}

/* LoginButton depends on Gigya data, that's why it needs to be client-side rendered only */
const LoginButton = ({ isLink = false, handler }: Props) => {
  const { t } = useBrandedTranslation()
  const { gigyaIsReady, showLoginPopup, isLoading } = useGigya()
  const { accountInfo } = useAuth()
  const gigyaIsEnabled = useRecoilValue(gigyaIsEnabledState)

  if (!gigyaIsEnabled) return

  const isUserLoggedIn = !!accountInfo.userId

  const handleLogin = async () => {
    /* Fire Google Analytics tracking event */
    trackEvent("genericEvent", "Micro Conversions", "Button Login", "Login")

    /* Optional additional handler to do something on login button click */
    if (handler) handler()

    /* Guard clause if Gigya is not loaded yet */
    if (!gigyaIsReady) return

    /* If user is not already logged in, show the Gigya login screen */
    if (!isUserLoggedIn) showLoginPopup()

    /* Global state and cookie are set in the event handler for the global onLogin
      event fired by Gigya, onLoginCallback */
  }

  return isUserLoggedIn ? (
    <>
      {/* Desktop */}
      <Box display={{ base: "none", md: "block" }} id="login-button-desktop">
        <CustomLink href={`/${pages.USER}`}>
          <IconButton
            icon={<UserAvatar firstName={accountInfo.firstName} lastName={accountInfo.lastName} />}
            bg="transparent"
            aria-label="User account"
            color="gray.900"
            _hover={{
              bgColor: "gray.200",
              ".avatar": { borderColor: "gray.900" },
            }}
            _active={{
              borderColor: "gray.900",
              bgColor: "gray.900",
              ".avatar": { color: "white", borderColor: "white", bgColor: "gray.900" },
            }}
          />
        </CustomLink>
      </Box>
      {/* Mobile and Tablet */}
      <Flex alignItems="center" gap="2" h="11" pl="1" display={{ base: "flex", md: "none" }}>
        <UserAvatar firstName={accountInfo.firstName} lastName={accountInfo.lastName} />
        <Text as="span">
          {accountInfo.firstName} {accountInfo.lastName}
        </Text>
      </Flex>
    </>
  ) : (
    <>
      {isLink ? (
        <Button
          variant="secondary"
          justifyContent="flex-start"
          px="2"
          iconSpacing="2"
          w={{ base: "100%", sm: "max-content" }}
          aria-label="Login"
          leftIcon={isLoading ? <LoadingIcon /> : <CArrowRight />}
          isDisabled={isLoading || !gigyaIsReady}
          onClick={handleLogin}
        >
          {t("acc-signInBtn")}
        </Button>
      ) : (
        <>
          {/* Desktop */}
          <IconButton
            id="login-button-desktop"
            aria-label="Login"
            icon={<CUserLoggedOff />}
            bg="transparent"
            color="gray.900"
            isLoading={isLoading}
            isDisabled={isLoading || !gigyaIsReady}
            onClick={handleLogin}
            display={{ base: "none", md: "inline-flex" }}
          />

          {/* Mobile and Tablet */}
          <Button
            variant="mobileMenu"
            iconSpacing="3"
            aria-label="Login"
            leftIcon={isLoading ? <LoadingIcon /> : <CUserLoggedOff />}
            isDisabled={isLoading || !gigyaIsReady}
            onClick={handleLogin}
            display={{ base: "inline-flex", md: "none" }}
          >
            {t("acc-signInBtn")}
          </Button>
        </>
      )}
    </>
  )
}

export default LoginButton
